/**
 * The dungeon run store. HTTP, not a socket: intents queue up, one batch is in flight at a
 * time, and the server's answer is the only truth about where things are.
 */
import type { DungeonEvent, DungeonIntent, DungeonView, RunEnd } from '@xox/shared'
import { create } from 'zustand'
import {
  BATCH_SIZE,
  canEnqueue,
  deriveDungeonEffects,
  type InFlight,
  RETRY_DELAYS_MS,
  reduceAct,
  reduceStale,
  STALE_WINDOW_MS,
} from '../dungeon/queue'
import { ApiError, api } from '../lib/api'
import { feedback } from '../lib/feedback'
import { useSession } from './session'

interface DungeonStore {
  runId: string | null
  view: DungeonView | null
  queue: DungeonIntent[]
  inFlight: InFlight | null
  /** Events the board has not animated yet. */
  fx: DungeonEvent[]
  end: RunEnd | null
  error: string | null
  /** Another tab seems to be playing this run. */
  conflict: boolean
  loading: boolean
  open: (runId: string) => Promise<void>
  close: () => void
  enqueue: (intent: DungeonIntent) => void
  flush: () => void
  retry: () => void
  takeFx: () => DungeonEvent[]
  dismissError: () => void
}

let staleAt: number[] = []
let retryTimer: ReturnType<typeof setTimeout> | null = null

export const useDungeon = create<DungeonStore>((set, get) => ({
  runId: null,
  view: null,
  queue: [],
  inFlight: null,
  fx: [],
  end: null,
  error: null,
  conflict: false,
  loading: false,

  async open(runId) {
    if (get().runId === runId && get().view) return
    if (retryTimer) clearTimeout(retryTimer)
    staleAt = []
    set({
      runId,
      view: null,
      queue: [],
      inFlight: null,
      fx: [],
      end: null,
      error: null,
      conflict: false,
      loading: true,
    })
    try {
      const { view } = await api.dungeonRun(runId)
      if (get().runId !== runId) return
      set({ view, loading: false })
    } catch (err) {
      set({ loading: false, error: (err as Error).message })
    }
  },

  close() {
    if (retryTimer) clearTimeout(retryTimer)
    retryTimer = null
    set({ runId: null, view: null, queue: [], inFlight: null, fx: [], end: null, error: null })
  },

  enqueue(intent) {
    const s = get()
    if (!canEnqueue(s, intent)) return
    set({ queue: [...s.queue, intent] })
    get().flush()
  },

  flush() {
    const s = get()
    if (s.inFlight || s.queue.length === 0 || !s.view || !s.runId) return
    const intents = s.queue.slice(0, BATCH_SIZE)
    const inFlight: InFlight = {
      batchId: crypto.randomUUID(),
      fromTurn: s.view.turn,
      intents,
      attempt: 0,
    }
    set({ queue: s.queue.slice(intents.length), inFlight })
    void send(inFlight)
  },

  retry() {
    const f = get().inFlight
    if (!f) return
    set({ error: null })
    void send({ ...f, attempt: 0 })
  },

  takeFx() {
    const fx = get().fx
    if (fx.length > 0) set({ fx: [] })
    return fx
  },

  dismissError() {
    set({ error: null })
  },
}))

async function send(f: InFlight) {
  const { runId } = useDungeon.getState()
  if (!runId) return
  try {
    const res = await api.dungeonAct(runId, {
      batchId: f.batchId,
      fromTurn: f.fromTurn,
      intents: f.intents,
    })
    const s = useDungeon.getState()
    if (s.inFlight?.batchId !== f.batchId) return
    const patch = reduceAct(s, res, f.intents.length)
    for (const effect of deriveDungeonEffects(res.events)) feedback(effect)
    useDungeon.setState({ ...patch, fx: [...s.fx, ...patch.fx] })
    if (res.end) void useSession.getState().load()
    useDungeon.getState().flush()
  } catch (err) {
    const s = useDungeon.getState()
    if (s.inFlight?.batchId !== f.batchId) return
    if (err instanceof ApiError && err.status === 409) {
      const body = err.body as { view?: DungeonView } | undefined
      const now = Date.now()
      staleAt = [...staleAt.filter((t) => now - t < STALE_WINDOW_MS), now]
      if (body?.view)
        useDungeon.setState({ ...reduceStale(body.view), conflict: staleAt.length >= 2 })
      else useDungeon.setState({ queue: [], inFlight: null })
      return
    }
    if (err instanceof ApiError && err.status === 400) {
      // The run is over (or the batch was malformed): resync from the server and stop.
      try {
        const { view } = await api.dungeonRun(runId)
        useDungeon.setState({ view, queue: [], inFlight: null })
      } catch {
        useDungeon.setState({ queue: [], inFlight: null, error: err.message })
      }
      return
    }
    const delay = RETRY_DELAYS_MS[f.attempt]
    if (delay !== undefined) {
      retryTimer = setTimeout(() => void send({ ...f, attempt: f.attempt + 1 }), delay)
      useDungeon.setState({ inFlight: { ...f, attempt: f.attempt + 1 } })
      return
    }
    useDungeon.setState({ error: 'Lost the connection. Your last move is waiting to be sent.' })
  }
}
